import React, { useEffect, useState } from "react";
import { Sparkle, Question, Lightbulb, ArrowRight, ArrowLeft, CheckCircle, Lifebuoy } from "@phosphor-icons/react";
import { Spinner } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Slider } from "../../components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { Progress } from "../../components/ui/progress";
import { toast } from "sonner";
import api, { money, apiErr } from "../../lib/api";

export default function PackageBuilder() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("none");
  const [prompt, setPrompt] = useState("");
  const [session, setSession] = useState(null);
  const [question, setQuestion] = useState(null);
  const [progress, setProgress] = useState(null);
  const [answer, setAnswer] = useState(null);
  const [help, setHelp] = useState("");
  const [packages, setPackages] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refine, setRefine] = useState("");

  useEffect(() => { api.get("/organizer/events").then((r) => setEvents(r.data.events)); }, []);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/package/start", { prompt, event_id: eventId === "none" ? null : eventId });
      setSession(data.session_id); setQuestion(data.question); setProgress(data.progress);
      setAnswer(data.question.recommended ?? (data.question.type === "multi" ? [] : ""));
      setPackages(null); setHelp("");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const submitAnswer = async () => {
    setBusy(true); setHelp("");
    try {
      const { data } = await api.post("/ai/package/answer", { session_id: session, question_id: question.id, answer });
      if (data.done) { setPackages(data.packages); setQuestion(null); setProgress(data.progress); }
      else { setQuestion(data.question); setProgress(data.progress); setAnswer(data.question.recommended ?? (data.question.type === "multi" ? [] : "")); }
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const getHelp = async (kind) => {
    try { const { data } = await api.post("/ai/package/help", { session_id: session, question_id: question.id, kind });
      setHelp(data.help); if (kind === "recommend" && data.recommended != null) setAnswer(data.recommended); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const doRefine = async () => {
    if (!refine.trim()) return;
    setBusy(true);
    try { const { data } = await api.post("/ai/package/refine", { session_id: session, command: refine }); setPackages(data.packages); setRefine(""); toast.success("Package updated."); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const purchase = async (tier) => {
    setBusy(true);
    try { const { data } = await api.post("/ai/package/purchase", { session_id: session, tier }); toast.success(data.message); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center gap-2 mb-2">
        <span className="grid place-items-center w-10 h-10 rounded-full bg-primary/10 text-primary"><Sparkle weight="fill" size={20} /></span>
        <h1 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight">Ask AI to Build My Event Package</h1>
      </div>
      <p className="text-muted-foreground mb-6">Answer a few quick questions and AI builds the best service package from our real catalogue.</p>

      {/* start */}
      {!session && !packages && (
        <div className="rounded-xl border border-border bg-card p-6 space-y-4">
          <div>
            <label className="text-sm font-medium mb-1.5 block">Link an event (optional)</label>
            <Select value={eventId} onValueChange={setEventId}>
              <SelectTrigger data-testid="pkg-event"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">No specific event</SelectItem>{events.map((e) => <SelectItem key={e.id} value={e.id}>{e.title}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">What do you want to achieve?</label>
            <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2} data-testid="pkg-prompt"
              placeholder='e.g. "800-person comedy show, I want maximum sales below ₹25,000"' />
          </div>
          <Button onClick={start} disabled={busy} className="rounded-full" data-testid="pkg-start">{busy ? "Starting…" : <>Start <ArrowRight className="ml-1.5" /></>}</Button>
        </div>
      )}

      {/* question */}
      {question && (
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-center justify-between mb-3">
            <span className="overline">Question {progress.current} of {progress.total}</span>
            <span className="text-xs text-muted-foreground">{progress.remaining} remaining</span>
          </div>
          <Progress value={(progress.current / progress.total) * 100} className="h-1.5 mb-5" />
          <h2 className="font-heading text-xl font-semibold mb-4" data-testid="pkg-question">{question.text}</h2>

          {question.type === "single" && (
            <div className="grid sm:grid-cols-2 gap-3">
              {question.options.map((o) => (
                <button key={o.value} onClick={() => setAnswer(o.value)} data-testid={`opt-${o.value}`}
                  className={`text-left p-4 rounded-lg border transition-colors ${answer === o.value ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border hover:border-foreground/30"}`}>
                  <div className="font-medium">{o.label}</div>
                </button>
              ))}
            </div>
          )}
          {question.type === "multi" && (
            <div className="space-y-2">
              {question.options.map((o) => {
                const checked = (answer || []).includes(o.value);
                return (
                  <label key={o.value} className="flex items-center gap-3 p-3 rounded-lg border border-border cursor-pointer">
                    <Checkbox checked={checked} onCheckedChange={(c) => setAnswer(c ? [...(answer || []), o.value] : (answer || []).filter((v) => v !== o.value))} data-testid={`opt-${o.value}`} />
                    {o.label}
                  </label>
                );
              })}
            </div>
          )}
          {question.type === "number" && <Input type="number" value={answer || ""} onChange={(e) => setAnswer(+e.target.value)} data-testid="pkg-number" />}
          {question.type === "budget" && (
            <div>
              <div className="font-heading text-3xl font-semibold text-primary mb-3">{money(answer || 0)}</div>
              <Slider min={5000} max={200000} step={1000} value={[answer || 25000]} onValueChange={(v) => setAnswer(v[0])} data-testid="pkg-budget" />
            </div>
          )}

          {help && <div className="mt-4 rounded-lg bg-muted p-3 text-sm flex gap-2"><Lightbulb className="text-amber-500 shrink-0 mt-0.5" size={16} /> {help}</div>}

          <div className="flex flex-wrap gap-2 mt-5">
            <Button onClick={submitAnswer} disabled={busy} className="rounded-full" data-testid="pkg-next">{busy ? "…" : progress.remaining === 0 ? "Build my package" : <>Next <ArrowRight className="ml-1.5" size={16} /></>}</Button>
            <Button variant="outline" onClick={() => getHelp("recommend")} className="rounded-full" data-testid="pkg-recommend"><Sparkle size={15} className="mr-1.5 text-primary" /> I'm not sure — recommend</Button>
            <Button variant="ghost" onClick={() => getHelp("explain")} data-testid="pkg-explain"><Question size={15} className="mr-1.5" /> I don't understand</Button>
          </div>
          <div className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground flex items-center gap-2">
            <Lifebuoy size={14} /> Need help? Continue with AI · Live Chat · WhatsApp · Call Helpline · Talk to an Expert
          </div>
        </div>
      )}

      {/* packages */}
      {packages && (
        <div>
          <div className="flex items-center gap-2 mb-4 text-emerald-600"><CheckCircle weight="fill" /> <span className="font-medium">Your tailored packages are ready</span></div>
          <div className="grid md:grid-cols-3 gap-4">
            {packages.map((p) => (
              <div key={p.tier} data-testid={`package-${p.tier}`} className={`rounded-xl border p-5 flex flex-col ${p.tier === "Recommended" ? "border-primary ring-1 ring-primary bg-primary/5" : "border-border bg-card"}`}>
                {p.tier === "Recommended" && <span className="text-xs px-2 py-0.5 rounded-full bg-primary text-white w-fit mb-2">Best value</span>}
                <h3 className="font-heading text-xl font-semibold">{p.tier}</h3>
                <p className="text-sm text-muted-foreground">{p.summary}</p>
                <div className="my-4 font-heading text-2xl font-bold">{money(p.total)}<span className="text-xs text-muted-foreground font-normal"> incl. GST</span></div>
                <ul className="space-y-2 text-sm flex-1">
                  {p.items.map((it) => (
                    <li key={it.service_id} className="flex gap-2"><CheckCircle className="text-primary shrink-0 mt-0.5" size={15} weight="fill" /><span><span className="font-medium">{it.name}</span>{it.quantity > 1 ? ` ×${it.quantity}` : ""}<div className="text-xs text-muted-foreground">{it.reason}</div></span></li>
                  ))}
                </ul>
                <div className="text-xs text-muted-foreground mt-3">Subtotal {money(p.subtotal)} + GST {money(p.gst)}</div>
                <Button onClick={() => purchase(p.tier)} disabled={busy} className="rounded-full mt-3" data-testid={`buy-package-${p.tier}`}>Buy {p.tier}</Button>
              </div>
            ))}
          </div>
          <div className="mt-6 flex gap-2">
            <Input value={refine} onChange={(e) => setRefine(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doRefine()} placeholder='Refine: "keep below ₹20,000", "add WhatsApp marketing", "remove staff"' data-testid="pkg-refine" />
            <Button variant="outline" onClick={doRefine} disabled={busy} className="rounded-full"><Sparkle size={15} className="mr-1.5 text-primary" /> Rebuild</Button>
          </div>
        </div>
      )}
    </div>
  );
}
